import NextLink from 'next/link'
import React, { FC } from 'react'
import styled from 'styled-components'

import { BagItem } from '@lojinha/bag'
import { Box, Center, Grid, Text } from '@lojinha/design-system'
import { normalizeProductUrl } from '@lojinha/helpers'
import { MarketableClassification, MarketableKind } from '@lojinha/palantir'
import { CalendarHeader } from '../../../components/calendar-header'
import { t } from '../../../dictionary'
import { formatters } from '../../../dictionary/pt-formatters'
import {
  numberToCurrency,
  numberToCurrencyAriaLabel,
} from '../../../helpers/formatNumber'
import { OriginList } from '../../../modules/display/types'
import { InsideDays } from '../../../modules/page-content/page-content.types'
import { getPeriodName, getSortedPeriods } from './logic'

const Divider = styled.div(
  ({ theme }) => `
    background: ${theme.colors.gray200};
    width: 100%;
    height: 1px;
    margin: ${theme.space.x24} 0 ${theme.space.x24};
  `
)

const ItemContainer = styled.div(
  ({ theme }) => `
   padding: ${theme.space.x24} ${theme.space.x16} 0 0;
   cursor: pointer;
   text-decoration: none;
   color: inherit;
  `
)

const GridContainer = styled.div(
  ({ theme }) => `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(calc(100% / 3), auto));

  ${theme.media.below('lg')} {
    grid-template-columns: repeat(auto-fill, minmax(50%, auto));
  }
  ${theme.media.below('md')} {
    grid-template-columns: repeat(auto-fill, minmax(100%, auto));
  }
`
)

type ProductDisplayProps = {
  product: {
    id: string
    imageUrl: string
    name: string
    price?: number
    sku: string
    seo: {
      url: string
    }
  }
  index: number
}

type KitListProps = {
  days?: InsideDays[]
}

export const KitsList: FC<KitListProps> = ({ days }) => {
  const ProductDisplay: FC<ProductDisplayProps> = ({ product, index }) => (
    <NextLink href={normalizeProductUrl(product.seo.url)} passHref>
      <ItemContainer>
        <BagItem
          id={product.id}
          imageUrl={product.imageUrl}
          name={product.name}
          price={product.price ?? 0}
          quantity={1}
          kind={MarketableKind.Kit}
          classification={MarketableClassification.Kit}
          sku={product.sku}
          hideAmountBox
          eventList={OriginList.LIST}
          positionInList={index}
          noWrap
        />
      </ItemContainer>
    </NextLink>
  )

  const CalendarKitList: FC = () => (
    <>
      {days?.map(({ dayNumber, periods }) => (
        <Box key={`kit-day-${dayNumber}-row`}>
          <GridContainer key={`kit-day-${dayNumber}`}>
            {getSortedPeriods(periods).map(
              ({
                products,
                totalKcal = 0,
                totalPrice = 0,
                totalWeight = 0,
                name,
              }) =>
                products?.map((product, index) => (
                  <Box
                    key={`kit-day-${dayNumber}-${index}-${product.id}`}
                    alignSelf="end"
                  >
                    {index === 0 && (
                      <CalendarHeader
                        dayNumber={dayNumber}
                        details={[
                          `${totalWeight}g`,
                          `${totalKcal}kcal`,
                          numberToCurrency(totalPrice),
                        ]}
                        detailsAriaLabel={[
                          formatters.weightAndUnit(totalWeight, t.grams),
                          formatters.weightAndUnit(totalKcal, t.kcal),
                          numberToCurrencyAriaLabel(totalPrice),
                        ]}
                        title={getPeriodName(name)}
                      />
                    )}
                    <ProductDisplay product={product} index={index} />
                  </Box>
                ))
            )}
          </GridContainer>
          <Divider key={`kit-day-${dayNumber}-divider`} />
        </Box>
      ))}
    </>
  )

  const NoCalendarKitList: FC = () => (
    <GridContainer>
      {days?.[0].periods?.[0]?.products?.map((product, index) => (
        <Box key={product.id} alignSelf="end">
          <ProductDisplay product={product} index={index} />
        </Box>
      ))}
    </GridContainer>
  )

  if (!days?.length) return <div />
  return (
    <Box py="x16">
      <Center>
        <Grid
          gridTemplateColumns={[
            'repeat(24, 1fr)',
            'repeat(24, 1fr)',
            'repeat(12, 1fr)',
          ]}
          htmlAttrs={{ style: { width: '100%', maxWidth: '1440px' } }}
        >
          <Grid gridColumn={['2 / 24', '2 / 24', '2 / 12']}>
            <Box paddingTop="x32" paddingBottom="x16">
              <Text isBold textSize="x2">
                {t.kitListTitle.toUpperCase()}
              </Text>
            </Box>
          </Grid>
          <Grid gridColumn={['2 / 24', '2 / 24', '2 / 12']}>
            {days.length === 1 && days[0].periods?.length === 1 ? (
              <NoCalendarKitList />
            ) : (
              <CalendarKitList />
            )}
          </Grid>
        </Grid>
      </Center>
    </Box>
  )
}
